/**
 * The one place the browser talks to the API (plan §04.3).
 *
 * Everything that must be true of every request is true here rather than at
 * each call site: the access token is attached, the `{ data, meta }` envelope
 * is unwrapped, an error body becomes a typed `ApiClientError`, and a single
 * expired-token retry happens invisibly.
 *
 * **The access token lives in a module variable, never in `localStorage` or
 * `sessionStorage`** (criterion 16). Web storage is readable by any script that
 * reaches the page, so a persisted bearer token turns one XSS into a stolen
 * session that outlives the tab. The refresh token is an `HttpOnly` cookie,
 * which JavaScript cannot read at all — so the durable half of the session is
 * out of reach and the readable half dies with the page.
 */

import { isApiError, type ApiError, type ApiErrorDetail, type ErrorCode, type PageMeta, type Paginated } from '@rd/shared';

import { refreshAccessToken } from '../auth/refreshCoordinator.js';

export const API_BASE = '/api/v1';

export class ApiClientError extends Error {
  readonly code: ErrorCode;
  readonly httpStatus: number;
  readonly details: ApiErrorDetail[];
  readonly requestId: string;
  readonly retryable: boolean;
  /** Seconds, from `Retry-After`. Present on 429 and some 503s. */
  readonly retryAfterSeconds: number | null;

  constructor(args: {
    code: ErrorCode;
    message: string;
    httpStatus: number;
    details?: ApiErrorDetail[];
    requestId?: string;
    retryable?: boolean;
    retryAfterSeconds?: number | null;
  }) {
    super(args.message);
    this.name = 'ApiClientError';
    this.code = args.code;
    this.httpStatus = args.httpStatus;
    this.details = args.details ?? [];
    this.requestId = args.requestId ?? '';
    this.retryable = args.retryable ?? false;
    this.retryAfterSeconds = args.retryAfterSeconds ?? null;
  }

  /**
   * Server validation, keyed by our field name, ready to bind to inputs.
   * Details without a field are dropped — they belong above the form, and the
   * caller reads `message` for those.
   */
  fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const d of this.details) {
      if (d.field && !(d.field in out)) out[d.field] = d.message;
    }
    return out;
  }
}

/** A transport failure — no HTTP status, because nothing answered. */
export class NetworkError extends Error {
  override readonly cause: unknown;
  constructor(cause: unknown) {
    super('The panel could not reach the server.');
    this.name = 'NetworkError';
    this.cause = cause;
  }
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
  /** Sent as `Idempotency-Key`. Every write should pass one. */
  idempotencyKey?: string;
  signal?: AbortSignal;
  /**
   * HTTP cache mode. Defaults to the browser's own behaviour.
   *
   * `'no-cache'` means *revalidate*, not *do not cache*: the browser still
   * stores the response and still sends `If-None-Match`, so a route that
   * carries an ETag answers 304 with no body. That is how a caller buys
   * freshness without buying bandwidth (QA defect D4-01).
   */
  cache?: RequestCache;
  /** Internal: set on the replay so one retry cannot become a loop. */
  _isRetry?: boolean;
}

/* ---------------------------------------------------------------- *
 * Session state, deliberately module-scoped and deliberately not persisted.
 * ---------------------------------------------------------------- */

let accessToken: string | null = null;
let tenantRef: string | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

/** Platform scope only — the header the API reads to select a tenant. */
export function setTenantRef(ref: string | null): void {
  tenantRef = ref;
}

export function getTenantRef(): string | null {
  return tenantRef;
}

export function __resetApiClient(): void {
  accessToken = null;
  tenantRef = null;
}

function buildUrl(path: string, query: RequestOptions['query']): string {
  const url = path.startsWith('/api') ? path : `${API_BASE}${path}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined) continue;
    params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

async function readError(response: Response): Promise<ApiClientError> {
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    // A body that is not JSON is a proxy or a crash, not our API. Falling
    // through leaves the generic branch below to describe it honestly rather
    // than inventing a code.
  }

  const retryAfter = Number(response.headers.get('Retry-After'));

  if (isApiError(body)) {
    const err = (body as ApiError).error;
    return new ApiClientError({
      code: err.code,
      message: err.message,
      httpStatus: response.status,
      details: err.details ?? [],
      requestId: err.requestId,
      retryable: err.retryable,
      retryAfterSeconds: Number.isFinite(retryAfter) ? retryAfter : null,
    });
  }

  return new ApiClientError({
    code: 'INTERNAL_ERROR',
    message: 'Something went wrong at our end.',
    httpStatus: response.status,
    retryable: response.status >= 500,
    retryAfterSeconds: Number.isFinite(retryAfter) ? retryAfter : null,
  });
}

async function rawRequest(path: string, opts: RequestOptions): Promise<Response> {
  const headers = new Headers({ Accept: 'application/json' });
  if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`);
  if (tenantRef) headers.set('X-Tenant-Ref', tenantRef);
  if (opts.idempotencyKey) headers.set('Idempotency-Key', opts.idempotencyKey);

  const init: RequestInit = {
    method: opts.method ?? 'GET',
    headers,
    // The refresh cookie is HttpOnly and same-origin; without this it is not
    // sent and every refresh fails.
    credentials: 'same-origin',
  };
  if (opts.signal) init.signal = opts.signal;
  if (opts.cache) init.cache = opts.cache;
  if (opts.body !== undefined) {
    if (opts.body instanceof FormData) {
      /**
       * Multipart (branding asset uploads). The browser mints the boundary
       * itself, so `Content-Type` must NOT be set here — setting it would ship
       * a boundary-less header the server cannot parse. The 401-refresh-replay
       * path below is unaffected: a `FormData` is a value object that `fetch`
       * serialises fresh on every call, so the single retry re-sends the same
       * parts rather than an already-consumed stream.
       */
      init.body = opts.body;
    } else {
      headers.set('Content-Type', 'application/json');
      init.body = JSON.stringify(opts.body);
    }
  }

  try {
    return await fetch(buildUrl(path, opts.query), init);
  } catch (err) {
    // An aborted request is the caller's own doing, not a network fault.
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw new NetworkError(err);
  }
}

/**
 * Issues a request, and on `401 AUTH_TOKEN_EXPIRED` refreshes once and replays.
 *
 * **Exactly one replay**, guarded by `_isRetry`. A refresh that succeeds and is
 * still met with a 401 means something other than expiry — a revoked token
 * version, a disabled account — and retrying that forever is how a client melts
 * a server during an incident.
 */
async function requestWithRefresh(path: string, opts: RequestOptions): Promise<Response> {
  const response = await rawRequest(path, opts);
  if (response.status !== 401 || opts._isRetry) return response;

  // Peek at the body without consuming the original: only `AUTH_TOKEN_EXPIRED`
  // is refreshable. `AUTH_INVALID_CREDENTIALS` from a login form must surface.
  const clone = response.clone();
  let code: string | null = null;
  try {
    const body: unknown = await clone.json();
    if (isApiError(body)) code = body.error.code;
  } catch {
    code = null;
  }
  if (code !== 'AUTH_TOKEN_EXPIRED') return response;

  await refreshAccessToken();
  return rawRequest(path, { ...opts, _isRetry: true });
}

export async function apiRequest<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const response = await requestWithRefresh(path, opts);
  if (!response.ok) throw await readError(response);
  if (response.status === 204) return undefined as T;

  const body: unknown = await response.json();
  return (body as { data: T }).data;
}

export async function apiPaginated<T>(
  path: string,
  opts: RequestOptions = {},
): Promise<Paginated<T>> {
  const response = await requestWithRefresh(path, opts);
  if (!response.ok) throw await readError(response);

  const body = (await response.json()) as { data: T[]; meta?: PageMeta };
  return {
    data: body.data,
    // A list route always sends meta; defaulting rather than throwing means a
    // missing one degrades to "one page" instead of a blank screen.
    meta: body.meta ?? { page: 1, pageSize: body.data.length, total: body.data.length },
  };
}

/**
 * For the one route that returns bytes (`GET /exports/{ref}/file`). Returns the
 * Blob rather than triggering a download, so the caller decides.
 */
export async function apiStream(path: string, opts: RequestOptions = {}): Promise<Blob> {
  const response = await requestWithRefresh(path, opts);
  if (!response.ok) throw await readError(response);
  return response.blob();
}

/**
 * A client-generated idempotency key. `crypto.randomUUID` is available in every
 * browser this app supports and in jsdom; the fallback exists only so a test
 * environment without it does not fail in a confusing way.
 */
export function newIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `idem-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}
